/**
 * Religious and cultural head coverings (adventure masters, 2026-11-04). A person whose photo shows a head covering
 * (hijab, headscarf, a dupatta worn over the head, turban, patka, kippah, kufi...) keeps it on every page and the
 * cover, whatever the master's wardrobe puts them in (an explorer's sun hat, a space helmet, scuba gear). The generic
 * wardrobe sentence "anyone who wears a headscarf keeps it" was ignored per person by the image model (QA book
 * 992557f6: the grandmother's dupatta drawn at home only), so the photo analysis now records the covering per person
 * and every picture prompt carries a NAMED line for them (headCoveringLine). Pure; client-, server- and worker-safe.
 */

/**
 * Words that make an analysis answer a head covering we keep (an allow-list: hats, caps and hair clips never pass).
 * "scarf" and "veil" count only when the answer says they are worn over the head or hair (a scarf round the neck, a
 * bridal veil never do).
 */
const COVERING_WORDS = [
  'hijab', 'headscarf', 'head scarf', 'dupatta', 'chunni', 'chunri', 'odhni', 'orhni', 'khimar', 'shayla',
  'tudung', 'kerudung', 'jilbab', 'chador', 'tichel', 'mitpachat', 'head wrap', 'headwrap', 'gele', 'duku',
  'doek', 'turban', 'patka', 'dastar', 'pagri', 'pagdi', 'pugree', 'safa', 'keski', 'kippah', 'kippa', 'yarmulke',
  'kufi', 'taqiyah', 'taqiya', 'prayer cap', 'topi', 'keffiyeh', 'ghutra', 'shemagh', 'mantilla', 'wimple',
];
/** Covering words that count only with "head" or "hair" in the answer. */
const ON_HEAD_ONLY = /^(scarf|veil)$/;
/** Wound coverings worn instead of a hat (a sun hat is never put on top of them). */
const TURBAN_WORDS = /\b(turban|patka|dastar|pagri|pagdi|pugree|safa|keski)\b/;
/** Small skullcaps: the same cap stays on in the water (never a swim headscarf). */
const CAP_WORDS = /\b(kippah|kippa|yarmulke|kufi|taqiyah|taqiya|topi|prayer cap)\b/;
const HELMET_WORDS = /\b(helmet|visor|spacesuit|space suit|space-suit)\b/i;
const WATER_WORDS = /\b(wetsuit|scuba|dive|diving|snorkel\w*|swim\w*|underwater|regulator|fins)\b/i;
/** Never a religious/cultural covering: for an occasion, a part or dressing up. */
const REJECT_RE = /\b(bridal|bride|wedding|costume|pirate|fancy|toy|dress-up)\b/;
/** Worn on the neck or shoulders: rejected unless the answer also says it is on the head (ON_HEAD_RE). */
const NECK_RE = /\b(neck|shoulders?)\b/;
/** The answer says the covering is worn on the head or hair. */
const ON_HEAD_RE = /\b(?:over|on|covering)\s+(?:the\s+|her\s+|his\s+|their\s+)?(?:head|hair)\b/;
/** A lookalike ("turban-style party hat", "hijab-like hood"): a covering word followed by -style/-like/... */
const LOOKALIKE_RE = /(?:-|\s)(?:style|like|shaped|inspired)\b/;
/** A negated answer ("no hijab", "none (not wearing a hijab)", "without a headscarf"). */
const NEGATION_RE = /^(?:no|none|not|without|n a|na|nothing)\b|\bnot\s+(?:on|over|covering)\b|\b(?:no|not|without)\s+(?:wearing\s+)?(?:an?\s+|any\s+)?(?:head\s*|religious\s+)?(?:covering|hijab|headscarf|scarf|veil|turban|cap|kippah|kufi|dupatta|wrap)\b/;
/** The only words kept around the covering word: colour, material, pattern and how it is worn. */
const DESCRIPTOR_WORDS = new Set((
  'white black beige cream ivory grey gray navy blue light dark pale sky deep bright red maroon burgundy pink rose ' +
  'purple lilac lavender violet green olive mint teal emerald yellow mustard gold golden orange saffron peach brown tan ' +
  'taupe khaki camel silver multicoloured multicolored colourful colorful patterned printed floral striped checked ' +
  'embroidered sequined plain sheer chiffon silk cotton lace knitted crocheted satin jersey linen wool woollen thin ' +
  'lightweight long short small large draped wrapped tied pinned folded worn loosely loose tightly neatly over on ' +
  'covering the a and with of her his their head hair prayer'
).split(' '));
/** Words a kept phrase never starts or ends with. */
const EDGE_WORDS = new Set('a an the and with of over on her his their draped wrapped tied pinned folded worn loosely loose tightly neatly covering'.split(' '));

const MAX_LEN = 48;
const MAX_WORDS = 8;

/** The analysis prompt line for the per-person head covering (services/multi-character-photo-analysis.ts). */
export const HEAD_COVERING_PROMPT_FIELD =
  '- headCovering: a religious or cultural head covering this person is wearing in the photo (hijab, headscarf, a dupatta or chunni worn over the head, khimar, head wrap, turban, patka, kippah, kufi, prayer cap...), described in a few plain words with its colour and how it is worn (e.g. "white sheer dupatta draped over the head", "beige hijab", "navy blue turban"); "" when there is none. Never report an ordinary hat, cap, party hat, hood, headband, hair clip or bow, a scarf around the neck, a bridal veil, a winter hood or hat, or anything that is not on the head.';

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
const COVERING_RE = new RegExp(`\\b(?:${COVERING_WORDS.map(escapeRe).join('|')})\\b`);

/**
 * The index range [from, to] of the first covering word in `words` (two-word coverings span two tokens); null when
 * there is none. A scarf/veil counts only when `onHead`.
 */
function coveringSpan(words: string[], onHead: boolean): [number, number] | null {
  for (let i = 0; i < words.length; i++) {
    if (i + 1 < words.length && COVERING_RE.test(`${words[i]} ${words[i + 1]}`) && !COVERING_RE.test(words[i]) && !COVERING_RE.test(words[i + 1])) return [i, i + 1];
    if (COVERING_RE.test(words[i])) return [i, i];
    if (onHead && ON_HEAD_ONLY.test(words[i])) return [i, i];
  }
  return null;
}

/**
 * One analysis (or request) value as a stored head covering, cut down to an allow-list form: the covering word plus
 * the colour/material/draping words right around it (at most 8 words and 48 characters), lower case. undefined when
 * it is empty, negated ("no hijab"), not on the head (a scarf round the neck, a bridal veil, a costume) or not a
 * covering we keep. No other text from the answer can reach an image prompt.
 */
export function normalizeHeadCovering(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const s = raw
    .toLowerCase()
    .replace(/[^a-z0-9\s-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!s || NEGATION_RE.test(s) || REJECT_RE.test(s)) return undefined;
  const onHead = ON_HEAD_RE.test(s);
  if (NECK_RE.test(s) && !onHead) return undefined;
  const words = s.split(' ');
  const span = coveringSpan(words, onHead);
  if (!span) return undefined;
  // "turban-style party hat", "hijab-like hood": a lookalike, not the covering
  if (LOOKALIKE_RE.test(`${words[span[1]]} ${words[span[1] + 1] ?? ''}`)) return undefined;
  // A scarf/veil worn on the head is stored as a head covering, so the meaning survives any cut
  if (span[0] === span[1] && ON_HEAD_ONLY.test(words[span[0]])) {
    let a = span[0];
    while (a - 1 >= 0 && DESCRIPTOR_WORDS.has(words[a - 1]) && !EDGE_WORDS.has(words[a - 1])) a--;
    const out = [...words.slice(a, span[0]), words[span[0]] === 'scarf' ? 'headscarf' : 'head veil'];
    while (out.length > 1 && (out.length > MAX_WORDS || out.join(' ').length > MAX_LEN)) out.shift();
    return out.join(' ');
  }
  const ok = (i: number) => DESCRIPTOR_WORDS.has(words[i]) || COVERING_RE.test(words[i]) || ON_HEAD_ONLY.test(words[i]);
  let a = span[0];
  let b = span[1];
  while (a - 1 >= 0 && ok(a - 1)) a--;
  while (b + 1 < words.length && ok(b + 1)) b++;
  // kept = words[a..b]; the covering occupies [ka, kb] inside it
  let kept = words.slice(a, b + 1);
  let ka = span[0] - a;
  let kb = span[1] - a;
  const trimEdges = () => {
    while (ka > 0 && EDGE_WORDS.has(kept[0])) { kept = kept.slice(1); ka--; kb--; }
    while (kept.length - 1 > kb && EDGE_WORDS.has(kept[kept.length - 1])) kept = kept.slice(0, -1);
  };
  trimEdges();
  // Over the cap: drop words from the longer far end, never the covering word
  while (kept.length > MAX_WORDS || kept.join(' ').length > MAX_LEN) {
    const after = kept.length - 1 - kb;
    if (after > 0 && after >= ka) kept = kept.slice(0, -1);
    else if (ka > 0) { kept = kept.slice(1); ka--; kb--; }
    else break;
    trimEdges();
  }
  return kept.join(' ').trim() || undefined;
}

/** The outfit part of a prompt description (after the last "Outfit on …:" or "Outfit:"); '' when there is none. */
export function outfitPartOf(description: string): string {
  const d = description || '';
  const re = /Outfit(?: on [^:]*)?:\s*/g;
  let last = -1;
  let r: RegExpExecArray | null;
  while ((r = re.exec(d))) last = r.index + r[0].length;
  return last >= 0 ? d.slice(last).trim() : '';
}

/** `{ headCovering }` when `raw` normalises to a head covering, else `{}` (spread into a stored character). */
export function withHeadCovering(raw: unknown): { headCovering?: string } {
  const headCovering = normalizeHeadCovering(raw);
  return headCovering ? { headCovering } : {};
}

/**
 * The NAMED per-person prompt line for a picture where `name` appears in `outfit` (this page's or the cover's outfit
 * text, when known). '' when the person has no head covering.
 */
export function headCoveringLine(name: string, covering: unknown, outfit?: string): string {
  const c = normalizeHeadCovering(covering);
  const who = (name || '').trim();
  if (!c || !who) return '';
  const own = `${who}'s ${c}`;
  const parts = [
    `HEAD COVERING (from the photo; it is part of ${who}, not the costume, and no costume rule removes it): ${who} always wears the ${c} exactly as in the photo (same kind, colour and way of wearing it), on every page, in every outfit, indoors and outdoors; ${who} is never bare-headed.`,
  ];
  const text = outfit || '';
  if (TURBAN_WORDS.test(c)) {
    parts.push(`No hat on top of it: the ${c} takes the place of any hat in the outfit.`);
  } else {
    parts.push(`Any hat sits over it (or ${who} wears no hat); it never replaces it.`);
  }
  if (HELMET_WORDS.test(text)) {
    parts.push(`Inside a helmet it stays on: ${own} is clearly visible through the clear visor, framing ${who}'s face.`);
  }
  if (WATER_WORDS.test(text) && CAP_WORDS.test(c)) {
    parts.push(`In the water the same small cap stays on (under any hood and the mask strap), never a bare head.`);
  } else if (WATER_WORDS.test(text) && !TURBAN_WORDS.test(c)) {
    parts.push(`In the water it is a snug swim headscarf in the same colour, under any hood and the mask strap, never a bare head.`);
  } else if (WATER_WORDS.test(text)) {
    parts.push(`In the water it is a snug fabric head covering in the same colour, under any hood and the mask strap, never a bare head.`);
  }
  return parts.join(' ');
}
